import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import type { FeatureKey } from '@/lib/featureFlags'
import { useFeatureFlags } from '@/providers/FeatureFlagsProvider'

/**
 * Wraps a route element and renders it only when every feature in `features` is
 * enabled for the SELECTED TENANT. Otherwise redirects to the "no access" page —
 * the same destination as a permission denial, because from the user's side both
 * are just "this is not available to you".
 *
 * `features` holds the route's HARD dependencies: the data sources without which
 * the screen has nothing to render. Features a screen merely uses for a widget
 * are deliberately absent and degrade in place instead (see `useFeatureFlag`) —
 * these modules are densely interdependent, so blocking on every dependency
 * would make one toggle blank half the app.
 *
 * Sibling to `Protected`, and composed outside it: permission first, then
 * feature. Three deliberate behaviors, each of which was a bug in the other
 * direction:
 *
 *   - HOLDS while the effective map is loading (renders null). Deciding early
 *     would redirect an entitled user to /no-access and never recover, since the
 *     redirect has already happened — the same failure `Protected` documents for
 *     an unresolved principal.
 *   - Denies ONLY on an explicit `false`. An UNKNOWN key counts as enabled, so a
 *     frontend deployed ahead of its backend (new route, catalog key not shipped
 *     yet) never blanks a page the backend is still happily serving.
 *   - Is not the enforcement boundary. The backend's `@RequireFeature` guard
 *     fails closed; this only stops the user from staring at a page whose every
 *     request 403s.
 */
export function FeatureGated({
  features,
  children,
}: {
  features: readonly FeatureKey[]
  children: ReactNode
}) {
  const { flags, isLoading } = useFeatureFlags()

  if (isLoading) {
    return null
  }

  if (features.some((feature) => flags[feature] === false)) {
    return <Navigate to='/no-access' replace />
  }
  return <>{children}</>
}
